import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateNoteMutation,
  useCreateLinkMetadataMutation,
} from "@/mutations/notes";
import { useDetectNoteTypeMutation } from "@/mutations/noteTypes";
import { useProcessNoteEntitiesMutation } from "@/mutations/entities";
import { subscribeToNoteProcessing } from "./useNoteProcessing";

interface NoteCreationData {
  title: string;
  content?: string;
  type?: string;
  dueDate?: Date;
}

export function useNoteCreation(userId: string) {
  const queryClient = useQueryClient();
  const { mutateAsync: createNote, isPending: isCreateNotePending } =
    useCreateNoteMutation();
  const createLinkMetadataMutation = useCreateLinkMetadataMutation();
  const { mutateAsync: detectNoteType } = useDetectNoteTypeMutation();
  const processEntitiesMutation = useProcessNoteEntitiesMutation();

  const handleNoteCreated = async (data: NoteCreationData) => {
    const noteWithUser = {
      title: data.title,
      content: data.content,
      type: data.type || "todo",
      dueDate: data.dueDate?.toISOString(),
      userId: userId,
    };

    const createdNote = await createNote(noteWithUser);

    if (createdNote.id) {
      if (data.type === "link") {
        const url = data.title || data.content || "";
        if (/^https?:\/\//.test(url)) {
          subscribeToNoteProcessing(createdNote.id, queryClient, {
            autoInvalidate: true,
          });

          createLinkMetadataMutation
            .mutateAsync({
              url,
              noteId: createdNote.id,
            })
            .catch((err: Error) => {
              console.error("[Link] Failed to unfurl link:", err);
            });
        }
      } else if (data.content) {
        void processEntitiesMutation.mutateAsync({
          noteId: createdNote.id,
          metadata: {},
        });
      }

      if (data.content) {
        void detectNoteType({
          noteId: createdNote.id,
          noteContent: data.content,
          noteTitle: data.title,
        });
      }
    }

    return createdNote;
  };

  return {
    handleNoteCreated,
    isPending: isCreateNotePending,
  };
}
